import { sleep } from "~/common/utils"

export type PollTaskOptions<TTask> = {
  fetchTask: () => Promise<TTask | null | undefined>
  getErrorMessage: (task: TTask) => string
  interval: number
  isDone: (task: TTask) => boolean
  isFailed: (task: TTask) => boolean
  isStale?: () => boolean
  maxAttempts: number
  onTask?: (task: TTask) => void
  signal?: AbortSignal
  timeoutMessage: string
}

export const pollTask = <TTask>({
  fetchTask,
  getErrorMessage,
  interval,
  isDone,
  isFailed,
  isStale,
  maxAttempts,
  onTask,
  signal,
  timeoutMessage,
}: PollTaskOptions<TTask>): Promise<TTask | undefined> => {
  return new Promise((resolve, reject) => {
    let attempt = 1

    const poll = async () => {
      if (attempt > maxAttempts) {
        throw new Error(timeoutMessage)
      }
      attempt += 1

      await sleep(interval, signal)

      if (isStale?.()) {
        resolve(undefined)
        return
      }

      const task = await fetchTask()

      if (isStale?.()) {
        resolve(undefined)
        return
      }

      if (!task) {
        scheduleNextPoll()
        return
      }

      onTask?.(task)

      if (isFailed(task)) {
        throw new Error(getErrorMessage(task))
      }

      if (isDone(task)) {
        resolve(task)
        return
      }

      scheduleNextPoll()
    }

    // Each round settles independently, so long polling does not retain a growing promise chain.
    const scheduleNextPoll = () => {
      void poll().catch(reject)
    }

    scheduleNextPoll()
  })
}
